import { supabase } from "@/lib/supabase";
import type {
  StoryExportRequestInput,
  StoryExportResult,
  StoryExportTemplateView,
} from "@/types/story";
import type { SubscriptionTier } from "@/types/projects";
import { isStoryExportTierAllowed } from "../../../../supabase/functions/_shared/story-export-tier.ts";

type StoryExportErrorCode =
  | "access-denied"
  | "target-not-found"
  | "template-forbidden"
  | "empty-content"
  | "failed";

type StoryExportErrorDetails = {
  recommendedTier?: SubscriptionTier;
  templateId?: string;
  tier?: SubscriptionTier;
};

const storyExportTemplates: StoryExportTemplateView[] = [
  {
    id: "classic-free",
    label: "Classic",
    description: "Balanced margins and a traditional manuscript look.",
    minimumTier: "free",
  },
  {
    id: "vellum-crafter",
    label: "Vellum",
    description: "A roomier page with softer line spacing for scene-driven drafts.",
    minimumTier: "crafter",
  },
  {
    id: "folio-crafter",
    label: "Folio",
    description: "Compact pagination suited to tighter chapter exports.",
    minimumTier: "crafter",
  },
  {
    id: "atlas-author",
    label: "Atlas",
    description: "Generous whitespace and book-like pacing for polished excerpts.",
    minimumTier: "author",
  },
  {
    id: "ledger-author",
    label: "Ledger",
    description: "A denser export suited to analytical or revision passes.",
    minimumTier: "author",
  },
];

export class StoryExportError extends Error {
  constructor(
    public readonly code: StoryExportErrorCode,
    message: string,
    public readonly details: StoryExportErrorDetails = {},
  ) {
    super(message);
    this.name = "StoryExportError";
  }
}

function parseContentDispositionFileName(headerValue: string | null) {
  if (!headerValue) {
    return null;
  }

  const matches = headerValue.match(/filename="(?<fileName>[^"]+)"/i);
  return matches?.groups?.fileName ?? null;
}

function sanitizeExportFileName(value: string) {
  const sanitized = value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return sanitized.length > 0 ? `${sanitized}.pdf` : "story-export.pdf";
}

async function readExportErrorCode(error: unknown) {
  if (typeof error === "object" && error && "context" in error) {
    const response = (error as { context?: Response }).context;
    if (response) {
      try {
        return (await response.text()).trim();
      } catch {
        return "";
      }
    }
  }

  return error instanceof Error ? error.message.trim() : "";
}

async function normalizeStoryExportError(error: unknown) {
  if (error instanceof StoryExportError) {
    return error;
  }

  const code = await readExportErrorCode(error);
  if (code.startsWith("STORY_EXPORT_TEMPLATE_FORBIDDEN|")) {
    const [, tier, templateId, recommendedTier] = code.split("|");

    return new StoryExportError("template-forbidden", code, {
      tier: tier as SubscriptionTier,
      templateId,
      recommendedTier: recommendedTier as SubscriptionTier,
    });
  }

  switch (code) {
    case "PROJECT_ACCESS_DENIED":
      return new StoryExportError("access-denied", code);
    case "STORY_EXPORT_TARGET_NOT_FOUND":
      return new StoryExportError("target-not-found", code);
    case "STORY_EXPORT_EMPTY_CONTENT":
      return new StoryExportError("empty-content", code);
    default:
      return new StoryExportError("failed", code || "STORY_EXPORT_FAILED");
  }
}

export function getStoryExportTemplatesForTier(tier: SubscriptionTier) {
  return storyExportTemplates.filter((template) =>
    isStoryExportTierAllowed(tier, template.minimumTier)
  );
}

export function formatStoryExportErrorMessage(error: StoryExportError) {
  switch (error.code) {
    case "access-denied":
      return "story.export.errors.accessDenied";
    case "target-not-found":
      return "story.export.errors.targetNotFound";
    case "template-forbidden":
      return "story.export.errors.templateForbidden";
    case "empty-content":
      return "story.export.errors.emptyContent";
    default:
      return "story.export.errors.failed";
  }
}

export async function exportStoryPiece(
  input: StoryExportRequestInput,
): Promise<StoryExportResult> {
  const { data, error, response } = await supabase.functions.invoke<Blob>("story-piece-export", {
    body: input,
  });

  if (error) {
    throw await normalizeStoryExportError(error);
  }

  if (!(data instanceof Blob)) {
    throw new StoryExportError("failed", "Expected a PDF response.");
  }

  return {
    blob: data,
    fileName:
      parseContentDispositionFileName(response?.headers.get("content-disposition") ?? null)
      ?? sanitizeExportFileName(input.targetId),
  };
}
